import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    Divider,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    InputAdornment,
    Button,
    CircularProgress
} from '@mui/material';
import { AccountBalanceWalletOutlined, CloseOutlined } from '@mui/icons-material';
import { motion } from 'framer-motion';

import { employeeCreditPaidApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';
import { formatINR } from './creditLimitsUtils';

const SettleRepaymentModal = ({ open, onClose, account, onSuccess }) => {
    const [settlementType, setSettlementType] = useState('full'); // 'full' | 'partial'
    const [partialAmount, setPartialAmount] = useState('');
    const [isSettling, setIsSettling] = useState(false);

    useEffect(() => {
        if (open) {
            setSettlementType('full');
            setPartialAmount('');
        }
    }, [open]);

    const handleSettleSubmit = async (e) => {
        e.preventDefault();
        if (!account) return;

        const outstanding = Number(account.current_outstanding ?? account.outstanding ?? 0);
        let amountToPay = 0;

        if (settlementType === 'full') {
            amountToPay = outstanding;
        } else {
            amountToPay = parseFloat(partialAmount) || 0;
            if (!partialAmount || isNaN(amountToPay)) {
                globalNotifyError('Please enter repayment amount');
                return;
            }
        }

        setIsSettling(true);
        try {
            const payload = {
                employee_id: account.id || account.employee_code || account.employee_id,
                id: account.id,
                email: account.email,
                amount: Number(amountToPay),
                paid_amount: Number(amountToPay),
                settlement_type: settlementType,
                type: settlementType
            };

            const response = await employeeCreditPaidApi(payload);

            if (response?.data?.success) {
                onClose();
                if (onSuccess) onSuccess();
            } else if (response?.data?.message) {
            } else {
                onClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error recording payment settlement:', error);
            handleCatchError(error);
        } finally {
            setIsSettling(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !isSettling && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fffbeb', color: '#b45309', display: 'flex' }}>
                        <AccountBalanceWalletOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Settle Repayment
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Record credit settlement / payment
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={onClose} disabled={isSettling}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSettleSubmit}>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2.5}>
                        {/* User & Outstanding Highlight Card */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block">
                                Account
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                                {account?.employee_name || account?.name || account?.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {account?.email}
                            </Typography>

                            <Divider sx={{ my: 1.5 }} />

                            <Typography variant="caption" color="text.secondary" display="block">
                                Current Outstanding
                            </Typography>
                            <Typography variant="h4" fontWeight={800} sx={{ color: '#d97706', mt: 0.25 }}>
                                {formatINR(account?.current_outstanding ?? account?.outstanding ?? 0)}
                            </Typography>
                        </Box>

                        {/* Settlement Type (Radio Buttons) */}
                        <FormControl component="fieldset">
                            <FormLabel sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, color: 'text.primary' }}>Settlement Type</FormLabel>
                            <RadioGroup value={settlementType} onChange={(e) => setSettlementType(e.target.value)}>
                                <FormControlLabel
                                    value="full"
                                    control={<Radio size="small" />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                Full Settlement
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Clears full balance ({formatINR(account?.current_outstanding ?? account?.outstanding ?? 0)})
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ mb: 1 }}
                                />
                                <FormControlLabel
                                    value="partial"
                                    control={<Radio size="small" />}
                                    label={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                Partial Amount
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Enter a partial payment amount
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>

                        {/* Dynamic Partial Amount Field */}
                        {settlementType === 'partial' && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                            >
                                <TextField
                                    label="Repayment Amount (₹)"
                                    required
                                    fullWidth
                                    type="number"
                                    value={partialAmount}
                                    onChange={(e) => setPartialAmount(e.target.value)}
                                    placeholder="e.g. 5000"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </motion.div>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={isSettling} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            isSettling ||
                            (settlementType === 'full' && Number(account?.current_outstanding ?? account?.outstanding ?? 0) <= 0) ||
                            (settlementType === 'partial' && !partialAmount)
                        }
                        startIcon={isSettling ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#d97706',
                            '&:hover': { bgcolor: '#b45309' }
                        }}
                    >
                        {isSettling ? 'Recording...' : 'Record Payment'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

SettleRepaymentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    account: PropTypes.object,
    onSuccess: PropTypes.func
};

export default SettleRepaymentModal;
